/**
 * Adapters from sidecar/backend socket payloads to app types.
 * Keep parsing tolerant because backend event payloads evolve.
 */

import type {
  AgentPresencePayload,
  CallEndedPayload,
  CallIncomingPayload,
  CallObserver,
  CallSnapshotAnsweredEntry,
  CallSnapshotPayload,
  CallSnapshotRingingEntry,
  CallTakenPayload,
  IncomingCallInfo,
  ObserverJoinedPayload,
  ObserverLeftPayload,
  OperatorLeftPayload,
  QueueClaimedPayload,
  QueueClaimReleasedPayload,
  SuperviseMode,
} from "~/services/calling/types";

/** Contract shape for Socket.IO call:incoming (API_CONTRACT §4). */
export type CallIncomingEventContract = {
  callerNumber: string;
  callUuid: string;
  deviceType: "ADMIN_TEST" | "DIGITAL_EMERGENCY";
  timestamp: string;
};

/** Contract shape for Operator WS operatorList (API_CONTRACT §3). */
export type OperatorListMessageContract = {
  type: "operatorList";
  operators: Array<{
    extension: string;
    userId: string;
    name: string;
    activeCalls: number;
    maxConcurrentCalls: number;
  }>;
};

function isObject(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function asString(value: unknown): string | undefined {
  if (typeof value === "string") {
    const t = value.trim();
    return t.length > 0 ? t : undefined;
  }
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return undefined;
}

function asNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const n = Number(value.trim());
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

function asBoolean(value: unknown): boolean | undefined {
  if (typeof value === "boolean") return value;
  if (typeof value === "number" && Number.isFinite(value)) {
    if (value === 1) return true;
    if (value === 0) return false;
    return undefined;
  }
  if (typeof value !== "string") return undefined;
  const normalized = value.trim().toLowerCase();
  if (["true", "yes", "y", "1", "on"].includes(normalized)) return true;
  if (["false", "no", "n", "0", "off"].includes(normalized)) return false;
  return undefined;
}

function asStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const result = value
    .map((item) => asString(item))
    .filter((item): item is string => Boolean(item));
  return result.length > 0 ? result : undefined;
}

function adaptRingGroupOperators(value: unknown) {
  if (!Array.isArray(value)) return undefined;
  const result = [];
  for (const item of value) {
    if (!isObject(item)) continue;
    const extension = asString(item.extension);
    const name = asString(item.name);
    const userId = asString(item.userId);
    if (!extension || !userId) continue;
    result.push({
      extension,
      name: name ?? extension,
      userId,
    });
  }
  return result.length > 0 ? result : undefined;
}

function asTimestampMs(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return undefined;
  const raw = value.trim();
  if (!raw) return undefined;
  const asNumberValue = Number(raw);
  if (Number.isFinite(asNumberValue)) return asNumberValue;
  const parsedDate = Date.parse(raw);
  return Number.isFinite(parsedDate) ? parsedDate : undefined;
}

/**
 * Map Socket.IO call:incoming payload to CallIncomingPayload.
 * Accepts both contract fields and backend compatibility aliases.
 */
export function adaptCallIncomingEvent(payload: unknown): CallIncomingPayload | null {
  if (!isObject(payload)) return null;
  const metadata = isObject(payload.metadata) ? payload.metadata : payload;
  const callUuid =
    asString(payload.callUuid) ??
    asString(payload.call_uuid) ??
    asString(payload.uniqueId) ??
    asString(payload.unique_id) ??
    asString(metadata.callUuid) ??
    asString(metadata.call_uuid) ??
    asString(metadata.uniqueId) ??
    asString(metadata.unique_id);
  const callId =
    asString(payload.callId) ??
    asString(payload.call_id) ??
    asString(payload.id) ??
    asString(metadata.callId) ??
    asString(metadata.call_id);
  const callerNumber =
    asString(payload.callerNumber) ??
    asString(payload.caller_number) ??
    asString(payload.caller) ??
    asString(payload.from) ??
    asString(payload.phoneNumber) ??
    asString(payload.phone_number) ??
    asString(metadata.callerNumber) ??
    asString(metadata.caller_number) ??
    asString(metadata.caller) ??
    asString(metadata.from) ??
    asString(metadata.phoneNumber) ??
    asString(metadata.phone_number);
  const service =
    asString(payload.service) ??
    asString(payload.deviceType) ??
    asString(payload.device_type) ??
    asString(payload.queue) ??
    asString(metadata.service) ??
    asString(metadata.deviceType) ??
    asString(metadata.device_type) ??
    asString(metadata.queue);
  const description =
    asString(payload.description) ??
    asString(payload.reason) ??
    asString(payload.message) ??
    asString(payload.event) ??
    asString(payload.type) ??
    "Voice Call";
  const signalType =
    asString(payload.signalType) ??
    asString(payload.signal_type) ??
    asString(payload.eventType) ??
    asString(payload.event_type) ??
    asString(payload.type);
  const requiresAnswerExplicit = asBoolean(
    payload.requiresAnswer ??
      payload.requires_answer ??
      payload.answerRequired ??
      payload.answer_required ??
      payload.needsAnswer ??
      payload.needs_answer
  );
  const noAnswerRequired = asBoolean(
    payload.noAnswerRequired ??
      payload.no_answer_required ??
      payload.isInformational ??
      payload.is_informational ??
      payload.informational ??
      payload.infoOnly ??
      payload.info_only
  );
  const requiresAnswer =
    typeof requiresAnswerExplicit === "boolean"
      ? requiresAnswerExplicit
      : typeof noAnswerRequired === "boolean"
        ? !noAnswerRequired
        : undefined;
  const ringGroup =
    asStringArray(payload.ringGroup) ??
    asStringArray(payload.ring_group) ??
    asStringArray(metadata.ringGroup) ??
    asStringArray(metadata.ring_group);
  const operators =
    adaptRingGroupOperators(payload.operators) ??
    adaptRingGroupOperators(metadata.operators);
  const deviceCid =
    asString(payload.deviceCid) ??
    asString(payload.device_cid) ??
    asString(metadata.deviceCid) ??
    asString(metadata.device_cid);
  const unitId =
    asString(payload.deviceUnitId) ??
    asString(payload.device_unit_id) ??
    asString(payload.unitId) ??
    asString(payload.unit_id) ??
    asString(metadata.deviceUnitId) ??
    asString(metadata.device_unit_id) ??
    asString(metadata.unitId) ??
    asString(metadata.unit_id);
  const operatorExtension =
    asString(payload.operatorExtension) ??
    asString(payload.operator_extension) ??
    asString(metadata.operatorExtension) ??
    asString(metadata.operator_extension);
  if (!callUuid && !callId && !callerNumber) return null;
  const receivedAtMs =
    asTimestampMs(
      payload.receivedAtMs ??
        payload.received_at_ms ??
        payload.timestamp ??
        payload.createdAt ??
        payload.created_at
    ) ?? Date.now();
  return {
    id: callUuid ?? callId ?? undefined,
    customerId:
      asString(payload.customerId) ??
      asString(payload.customer_id) ??
      asString(payload.clientId) ??
      asString(payload.client_id) ??
      asString(metadata.customerId) ??
      asString(metadata.customer_id) ??
      asString(metadata.clientId) ??
      asString(metadata.client_id) ??
      undefined,
    callUuid: callUuid ?? undefined,
    callId: callId ?? undefined,
    callerNumber: callerNumber ?? undefined,
    customerName:
      asString(payload.customerName) ??
      asString(payload.customer_name) ??
      asString(payload.name) ??
      asString(metadata.customerName) ??
      asString(metadata.customer_name) ??
      asString(metadata.name) ??
      undefined,
    service: service ?? undefined,
    unitId: unitId ?? undefined,
    description,
    signalType: signalType ?? undefined,
    requiresAnswer: requiresAnswer ?? undefined,
    metadata: {
      ...metadata,
      ...(deviceCid ? { deviceCid } : {}),
      ...(operatorExtension ? { operatorExtension } : {}),
      ...(ringGroup ? { ringGroup } : {}),
      ...(operators ? { operators } : {}),
    },
    receivedAtMs: Number.isFinite(receivedAtMs) ? receivedAtMs : Date.now(),
    deviceCid: deviceCid ?? undefined,
    operatorExtension: operatorExtension ?? undefined,
    ringGroup: ringGroup ?? undefined,
    operators: operators ?? undefined,
  };
}

/** Map Socket.IO call:taken payload to CallTakenPayload. */
export function adaptCallTakenEvent(payload: unknown): CallTakenPayload | null {
  if (!isObject(payload)) return null;
  const uniqueId =
    asString(payload.uniqueId) ??
    asString(payload.unique_id) ??
    asString(payload.callUuid) ??
    asString(payload.call_uuid);
  if (!uniqueId) return null;
  const takenByRaw = payload.takenBy ?? payload.taken_by;
  if (!isObject(takenByRaw)) return null;
  const extension = asString(takenByRaw.extension);
  const userId = asString(takenByRaw.userId ?? takenByRaw.user_id);
  if (!extension || !userId) return null;
  const name =
    asString(takenByRaw.name) ??
    asString(takenByRaw.displayName) ??
    extension;
  const promotedFrom =
    asString(payload.promotedFrom) ?? asString(payload.promoted_from) ?? undefined;

  return {
    uniqueId,
    takenBy: { extension, name, userId },
    caller:
      asString(payload.caller) ??
      asString(payload.callerNumber) ??
      asString(payload.caller_number) ??
      undefined,
    at:
      asString(payload.at) ??
      asString(payload.answeredAt) ??
      asString(payload.answered_at) ??
      new Date().toISOString(),
    ...(promotedFrom ? { promotedFrom } : {}),
  };
}

/** Map Socket.IO call:operator-left payload to OperatorLeftPayload. */
export function adaptOperatorLeftEvent(payload: unknown): OperatorLeftPayload | null {
  if (!isObject(payload)) return null;

  const uniqueId =
    asString(payload.uniqueId) ??
    asString(payload.unique_id) ??
    asString(payload.callUuid) ??
    asString(payload.call_uuid);
  if (!uniqueId) return null;

  const extension = asString(payload.extension);
  if (!extension) return null;

  return {
    uniqueId,
    extension,
    at: asString(payload.at) ?? new Date().toISOString(),
  };
}

function adaptSnapshotRingingEntry(payload: unknown): CallSnapshotRingingEntry | null {
  if (!isObject(payload)) return null;
  const uniqueId =
    asString(payload.uniqueId) ??
    asString(payload.unique_id) ??
    asString(payload.callUuid) ??
    asString(payload.call_uuid);
  if (!uniqueId) return null;
  return {
    uniqueId,
    caller:
      asString(payload.caller) ??
      asString(payload.callerNumber) ??
      asString(payload.caller_number) ??
      undefined,
    callType:
      asString(payload.callType) ??
      asString(payload.call_type) ??
      asString(payload.deviceType) ??
      asString(payload.device_type) ??
      undefined,
    deviceCid:
      asString(payload.deviceCid) ?? asString(payload.device_cid) ?? undefined,
    startedAt:
      asString(payload.startedAt) ??
      asString(payload.started_at) ??
      asString(payload.timestamp) ??
      undefined,
    ringGroup:
      asStringArray(payload.ringGroup) ?? asStringArray(payload.ring_group),
  };
}

function adaptSnapshotObserver(item: unknown): CallObserver | null {
  if (!isObject(item)) return null;
  const extension = asString(item.extension);
  if (!extension) return null;
  const modeRaw = asString(item.mode);
  const mode: SuperviseMode =
    modeRaw === "listen" || modeRaw === "barge" ? modeRaw : "listen";
  return {
    extension,
    name: asString(item.name) ?? extension,
    mode,
    joinedAt: asString(item.joinedAt) ?? asString(item.joined_at) ?? new Date().toISOString(),
  };
}

function adaptSnapshotObservers(value: unknown): CallObserver[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const result = value
    .map(adaptSnapshotObserver)
    .filter((item): item is CallObserver => Boolean(item));
  return result.length > 0 ? result : undefined;
}

function adaptSnapshotAnsweredEntry(
  payload: unknown
): CallSnapshotAnsweredEntry | null {
  if (!isObject(payload)) return null;
  const uniqueId =
    asString(payload.uniqueId) ??
    asString(payload.unique_id) ??
    asString(payload.callUuid) ??
    asString(payload.call_uuid);
  if (!uniqueId) return null;
  const takenByRaw = payload.takenBy ?? payload.taken_by;
  let takenBy: CallSnapshotAnsweredEntry["takenBy"];
  if (isObject(takenByRaw)) {
    const extension = asString(takenByRaw.extension);
    const userId = asString(takenByRaw.userId ?? takenByRaw.user_id);
    if (extension && userId) {
      takenBy = {
        extension,
        userId,
        name:
          asString(takenByRaw.name) ??
          asString(takenByRaw.displayName) ??
          extension,
      };
    }
  }
  const isMineExplicit = asBoolean(payload.isMine ?? payload.is_mine);
  return {
    uniqueId,
    caller:
      asString(payload.caller) ??
      asString(payload.callerNumber) ??
      asString(payload.caller_number) ??
      undefined,
    callType:
      asString(payload.callType) ??
      asString(payload.call_type) ??
      asString(payload.deviceType) ??
      asString(payload.device_type) ??
      undefined,
    deviceCid:
      asString(payload.deviceCid) ?? asString(payload.device_cid) ?? undefined,
    startedAt:
      asString(payload.startedAt) ??
      asString(payload.started_at) ??
      asString(payload.timestamp) ??
      undefined,
    answeredAt:
      asString(payload.answeredAt) ??
      asString(payload.answered_at) ??
      undefined,
    takenBy,
    isMine: typeof isMineExplicit === "boolean" ? isMineExplicit : undefined,
    ringGroup:
      asStringArray(payload.ringGroup) ?? asStringArray(payload.ring_group),
    observers: adaptSnapshotObservers(payload.observers),
  };
}

/** Map Socket.IO call:snapshot payload to CallSnapshotPayload. */
export function adaptCallSnapshotEvent(payload: unknown): CallSnapshotPayload | null {
  if (!isObject(payload)) return null;
  const ringingRaw = payload.ringing;
  const answeredRaw = payload.answered;
  const activeOperatorsRaw = payload.activeOperators ?? payload.active_operators;
  const ringing = Array.isArray(ringingRaw)
    ? ringingRaw
        .map(adaptSnapshotRingingEntry)
        .filter((entry): entry is CallSnapshotRingingEntry => Boolean(entry))
    : [];
  const answered = Array.isArray(answeredRaw)
    ? answeredRaw
        .map(adaptSnapshotAnsweredEntry)
        .filter((entry): entry is CallSnapshotAnsweredEntry => Boolean(entry))
    : [];
  const activeOperators = Array.isArray(activeOperatorsRaw)
    ? activeOperatorsRaw
        .map((item) => {
          if (!isObject(item)) return null;
          const extension = asString(item.extension);
          if (!extension) return null;
          return {
            extension,
            name: asString(item.name) ?? extension,
            activeCalls: asNumber(item.activeCalls ?? item.active_calls) ?? 0,
            maxConcurrentCalls:
              asNumber(item.maxConcurrentCalls ?? item.max_concurrent_calls) ?? 1,
          };
        })
        .filter(
          (
            entry
          ): entry is NonNullable<CallSnapshotPayload["activeOperators"][number]> =>
            Boolean(entry)
        )
    : [];
  return {
    at: asString(payload.at) ?? new Date().toISOString(),
    ringing,
    answered,
    activeOperators,
  };
}

export function mapSnapshotRingingToIncomingCall(
  entry: CallSnapshotRingingEntry
): IncomingCallInfo {
  const receivedAtMs =
    asTimestampMs(entry.startedAt) ?? Date.now();
  return {
    id: entry.uniqueId,
    uniqueId: entry.uniqueId,
    callUuid: entry.uniqueId,
    callId: entry.uniqueId,
    callerNumber: entry.caller ?? null,
    customerName: null,
    service: entry.callType ?? null,
    description: "Voice Call",
    signalType: null,
    requiresAnswer: true,
    metadata: {
      ringGroup: entry.ringGroup,
      deviceCid: entry.deviceCid,
    },
    receivedAtMs,
    deviceCid: entry.deviceCid ?? null,
    ringGroup: entry.ringGroup ?? null,
  };
}

/** Map Socket.IO call:ended payload to CallEndedPayload. */
export function adaptCallEndedEvent(payload: unknown): CallEndedPayload | null {
  if (!isObject(payload)) return null;
  const uniqueId =
    asString(payload.uniqueId) ??
    asString(payload.unique_id) ??
    asString(payload.callUuid) ??
    asString(payload.call_uuid);
  if (!uniqueId) return null;
  const endedAt =
    asString(payload.endedAt) ??
    asString(payload.ended_at) ??
    new Date().toISOString();
  return {
    uniqueId,
    extension: asString(payload.extension) ?? "",
    endedAt,
  };
}

/**
 * Map Operator WS operatorList message (contract shape) to AgentPresencePayload[].
 * Uses only extension, userId, name, activeCalls, maxConcurrentCalls.
 */
export function adaptOperatorListMessage(payload: unknown): AgentPresencePayload[] | null {
  if (!isObject(payload)) return null;
  if (payload.type !== "operatorList") return null;
  const operators = payload.operators;
  if (!Array.isArray(operators)) return null;
  const result: AgentPresencePayload[] = [];
  for (const op of operators) {
    if (!isObject(op)) continue;
    const extension = asString(op.extension);
    const userId = asString(op.userId);
    const name = asString(op.name);
    const activeCalls = asNumber(op.activeCalls) ?? 0;
    const maxConcurrentCalls = asNumber(op.maxConcurrentCalls) ?? 1;
    const id = userId ?? extension ?? "";
    if (!id) continue;
    const status =
      activeCalls >= maxConcurrentCalls ? "busy" : "online";
    result.push({
      id,
      name: name ?? id,
      status,
      sipExtension: extension ?? null,
      sipUri: null,
      profileImageUrl: null,
    });
  }
  return result.length > 0 ? result : null;
}

/** Map Socket.IO call:observer-joined payload to ObserverJoinedPayload. */
export function adaptObserverJoinedEvent(payload: unknown): ObserverJoinedPayload | null {
  if (!isObject(payload)) return null;

  const uniqueId =
    asString(payload.uniqueId) ??
    asString(payload.unique_id) ??
    asString(payload.callUuid) ??
    asString(payload.call_uuid);
  if (!uniqueId) return null;

  const observerRaw = payload.observer;
  if (!isObject(observerRaw)) return null;

  const extension = asString(observerRaw.extension);
  if (!extension) return null;

  const modeRaw = asString(observerRaw.mode);
  const mode: SuperviseMode =
    modeRaw === "listen" || modeRaw === "barge" ? modeRaw : "listen";

  return {
    uniqueId,
    observer: {
      extension,
      name: asString(observerRaw.name) ?? extension,
      mode,
      joinedAt:
        asString(observerRaw.joinedAt) ??
        asString(observerRaw.joined_at) ??
        new Date().toISOString(),
    },
    at: asString(payload.at) ?? new Date().toISOString(),
  };
}

/** Map Socket.IO call:observer-left payload to ObserverLeftPayload. */
export function adaptObserverLeftEvent(payload: unknown): ObserverLeftPayload | null {
  if (!isObject(payload)) return null;

  const uniqueId =
    asString(payload.uniqueId) ??
    asString(payload.unique_id) ??
    asString(payload.callUuid) ??
    asString(payload.call_uuid);
  if (!uniqueId) return null;

  const extension = asString(payload.extension);
  if (!extension) return null;

  return {
    uniqueId,
    extension,
    at: asString(payload.at) ?? new Date().toISOString(),
  };
}

/** Map Socket.IO queue:claimed payload. */
export function adaptQueueClaimedEvent(payload: unknown): QueueClaimedPayload | null {
  if (!isObject(payload)) return null;

  const uniqueId =
    asString(payload.uniqueId) ??
    asString(payload.unique_id) ??
    asString(payload.callUuid) ??
    asString(payload.call_uuid);
  const extension =
    asString(payload.extension) ??
    asString(payload.operatorExtension) ??
    asString(payload.operator_extension);
  if (!uniqueId || !extension) return null;

  return {
    uniqueId,
    extension,
    operatorName:
      asString(payload.operatorName) ?? asString(payload.operator_name) ?? null,
    at:
      asString(payload.at) ??
      asString(payload.claimedAt) ??
      asString(payload.claimed_at) ??
      null,
  };
}

/** Map Socket.IO queue:claim-released payload. */
export function adaptQueueClaimReleasedEvent(
  payload: unknown
): QueueClaimReleasedPayload | null {
  if (!isObject(payload)) return null;

  const uniqueId =
    asString(payload.uniqueId) ??
    asString(payload.unique_id) ??
    asString(payload.callUuid) ??
    asString(payload.call_uuid);
  if (!uniqueId) return null;

  return {
    uniqueId,
    extension:
      asString(payload.extension) ??
      asString(payload.operatorExtension) ??
      asString(payload.operator_extension) ??
      null,
    at: asString(payload.at) ?? null,
  };
}
